/**
 * BI — ВИДЖЕТЭЭС ДЭЛГЭРЭНГҮЙ РҮҮ (DRILL-DOWN) — ГЭРЭЭ (docs/31 §4v, docs/48 §11).
 *
 * ! Эзэмшигчийн шийдвэр (2026-09-27, эцсийн): BI-ийн виджет доторх мөр/зүсэм дээр дарахад ERP жагсаалт руу ҮСРЭХГҮЙ
 * ! (`/erp/1c-detail?…` — буруу). BI ДОТРОО тухайн зүйлийн ВИРТУАЛ ДЭЛГЭРЭНГҮЙ дэлгэц самбарын хажууд НЭМЭЛТ ТАБ болж нээгдэнэ
 * ! (хөтчийн таб шиг: давхарлагдана, X-ээр хаагдана, самбарын таб үлдэнэ). Энэ нь БҮХ виджетийн дэлгэрэнгүйд хамаарна.
 *
 * ! ХАЯГ (тохиролцсон): `/bi/{domain}/{entity-plural}/{id}` — `/bi/sales/products/<uuid>`. ЗАМД эх виджет («top») ОРОХГҮЙ:
 * ! «шилдэг» нь барааг ХААНААС нээснийг заана, таних тэмдэг биш — нэг барааг «Шилдэг бараа», шүүлт, хайлтаас нээсэн ч хаяг ИЖИЛ.
 * ! Хугацаа/шүүлт query-д (`?from&to&cmp&grain&<facet>…&tier`) — хуулсан холбоос ижил харагдац сэргээнэ.
 *
 * ! ЭНЭ ФАЙЛ = ТӨРЛИЙН ЖАГСААЛТ + ХАЯГИЙН ДҮРЭМ (API, админ, AI модуль ХУВААЛЦАНА). Админы бүртгэл (шошго тайлагч, нэмэлт хэсэг,
 * ! мөрийн дүрэм) — `apps/admin/src/views/apps/bi/drill/drill-registry.ts`. Хаяг нь AI модульд КАНОНИК ГҮН ХОЛБООС (docs/48 §11):
 * ! төрөл, хаяг НЭМЖ л болно — нэр/олон тоо СОЛИХГҮЙ (хуваалцсан холбоос, хэрэглэгчийн табын сан эвдэрнэ).
 *
 * ! ЦЭВЭР — React, Node, `Date` байхгүй (API ба админ хоёулаа импортлоно).
 */

import type { BiDomainKey, BiTier } from './bi.js'
import { BI_DOMAIN_KEYS } from './bi.js'
import type { BiRedactSpec } from './bi-tier.js'

/* ─────────────────────────── Таних тэмдгийн хэлбэр ─────────────────────────── */

/**
 * `uuid` — 1С-ийн `_IDRRef` (lookups/breakdown-той ИЖИЛ хэлбэр: жижиг үсэг, зураастай);
 * `int` — манай Postgres-ийн BigInt (машин);
 * `intOrNone` — ХН-ийн бүлэг: BigInt эсвэл `none` («Хэлтэсгүй» — ХН-ийн жагсаалтын `NONE_ID`, facet ч уншдаг).
 */
export const BI_DRILL_ID_FORMATS = ['uuid', 'int', 'intOrNone'] as const

export type BiDrillIdFormat = (typeof BI_DRILL_ID_FORMATS)[number]

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

const INT_RE = /^[1-9]\d{0,18}$/

/** ХН-ийн хоосон бүлгийн утга — `apps/admin/.../hr` жагсаалтын `NONE_ID`, `domain-row-links.ts`-ийн `NONE`-той ИЖИЛ. */
export const BI_DRILL_NONE_ID = 'none'

/* ─────────────────────────── Төрөл ─────────────────────────── */

/**
 * НЭГ ТӨРӨЛ (entity kind).
 *
 * ! `facet` — энэ төрлийн ДЭЛГЭРЭНГҮЙ = ТУХАЙН ДОМЭЙНЫ САМБАР энэ facet-ээр НАРИЙССАН (анхдагч drill хуудас: самбарын фреймворк,
 * ! хэсэг, хавтан, түвшин, A4/A3 хэвлэл, экспорт, «Хэрэгсэл» ДАХИН АШИГЛАНА) + толгойн карт. Facet нь эх цэгүүдийн query-ийн
 * ! түлхүүр (`/erp/1c-detail/*`-ийн `DETAIL_FACETS`, ХН-ийн `departmentId` …) — ЖАГСААЛТТАЙ ИЖИЛ нэр (Ш9 тэнцэл).
 * ! `minTier` — хуудсыг НЭЭЖ болох хамгийн бага түвшин (хүн/багийн хэмжээс — `salesManager` нь Pro facet (D7-1 b), Менежерт
 * ! сувгийн дотор нээлттэй (D8-5); санхүү бүхэлдээ ≥ Менежер). Хуудсан доторх тоо БҮР ӨӨРИЙН цэгийн хаалгаар (буулгалт, 403).
 * ! `permissions` — any-of: тухайн төрлийн тоог уншиж чадах эх модулийн эрх (D0 (А)); эрхгүй бол мөр нь drill болохгүй.
 */
export type BiDrillKindSpec = {
  domain: BiDomainKey

  /** URL-ийн сегмент — kebab-case, ОЛОН ТОО (`products`, `product-groups`). Домэйн дотроо давтагдахгүй (spec). */
  plural: string

  /** Самбарыг нарийсгах facet (эх цэгийн query-ийн түлхүүр). */
  facet: string

  idFormat: BiDrillIdFormat
  minTier: BiTier
  permissions: readonly string[]

  /** i18n түлхүүр — төрлийн нэр (табын tooltip, толгойн «Бараа»). */
  label: string
}

/** Борлуулалтын 1С-ийн уншилт — `/erp/1c-detail/*`-ийн `BI_SALES_READ`-тэй ИЖИЛ (Ү7). */
const SALES_READ = ['erp.reports:list', 'erp.reports:aggregate'] as const

/** ХН-ийн хэсгүүдийн аль нэгийг унших эрх (ажиллах хүч / ирц / чөлөө). */
const HR_READ = ['hr.employees:list', 'timesheet.attendance:list', 'timesheet.leave:list'] as const

/**
 * ТӨРЛИЙН БҮРТГЭЛ. Түлхүүр = `<domain>.<entity>` (entity нь эх хэмжээсийн нэр — breakdown-ийн `by`).
 *
 * ! Хүснэгт нь docs/31 §4v.2-ын «Төрлийн бүртгэл»-тэй 1:1. Мөрийн дүрэм (аль виджетийн аль мөр аль төрөл рүү) — админы
 * ! `BI_ROW_RULES` (docs/31 §4v.1 бараа тооллого).
 */
export const BI_DRILL_KINDS = {
  /* ── Борлуулалт (1С; facet = `DETAIL_FACETS`; `product`, `customer` ШИНЭ — API ба `/erp/1c-detail` жагсаалт ХАМТ) ── */
  'sales.product': {
    domain: 'sales',
    plural: 'products',
    facet: 'product',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.product'
  },
  'sales.customer': {
    domain: 'sales',
    plural: 'customers',
    facet: 'customer',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.customer'
  },
  'sales.brand': {
    domain: 'sales',
    plural: 'brands',
    facet: 'brand',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.brand'
  },
  'sales.productGroup': {
    domain: 'sales',
    plural: 'product-groups',
    facet: 'productGroup',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.productGroup'
  },
  'sales.manufacturer': {
    domain: 'sales',
    plural: 'manufacturers',
    facet: 'manufacturer',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.manufacturer'
  },
  'sales.warehouse': {
    domain: 'sales',
    plural: 'warehouses',
    facet: 'warehouse',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.warehouse'
  },
  'sales.businessRegion': {
    domain: 'sales',
    plural: 'business-regions',
    facet: 'businessRegion',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.businessRegion'
  },
  'sales.deliveryZone': {
    domain: 'sales',
    plural: 'delivery-zones',
    facet: 'deliveryZone',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.deliveryZone'
  },
  'sales.salesChannel': {
    domain: 'sales',
    plural: 'sales-channels',
    facet: 'salesChannel',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.salesChannel'
  },
  'sales.outletType': {
    domain: 'sales',
    plural: 'outlet-types',
    facet: 'outletType',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.outletType'
  },
  'sales.partnerType': {
    domain: 'sales',
    plural: 'partner-types',
    facet: 'partnerType',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.partnerType'
  },

  /*
   * ! Борлуулалтын БАГ = 1С хэрэглэгч (маршрутын баг, хүн биш — docs/46 §6.4). Pro facet (D7-1 b) → Менежерт сувгийн дотор (D8-5).
   * ! docs/31 §4w: «Борлуулалтын баг (маршрут)» — `manager` («Үндсэн менежер») нэгдсэн (хасагдсан). Табын шошго = МАРШРУТЫН НЭР
   * ! (`Route101_Team2`, `MPG_ROUTE_101_TEAM1`) — `/erp/1c-detail/lookups`-ийн `salesManager` сонголтоос (`BiSalesTeamOption`).
   * ! Хаяг ХЭВЭЭР `/bi/sales/teams/{id}` (id = `/erp/routes`-ийн uuid). «Маршрутгүй» мөр (`BI_NON_ROUTE_ID`) drill ТӨРӨЛ БИШ —
   * ! `memberIds`-ээр самбарыг нарийсгана.
   */
  'sales.team': {
    domain: 'sales',
    plural: 'teams',
    facet: 'salesManager',
    idFormat: 'uuid',
    minTier: 'manager',
    permissions: SALES_READ,
    label: 'bi.drill.kind.sales.team'
  },

  /* ── ХН — ЗӨВХӨН БҮЛЭГ (хэлтэс, байгууллага, албан тушаал, байршил). Хувь хүн рүү ХЭЗЭЭ Ч drill хийхгүй (docs/31 §4v.1). ── */
  'hr.department': {
    domain: 'hr',
    plural: 'departments',
    facet: 'departmentId',
    idFormat: 'intOrNone',
    minTier: 'basic',
    permissions: HR_READ,
    label: 'bi.drill.kind.hr.department'
  },
  'hr.organization': {
    domain: 'hr',
    plural: 'organizations',
    facet: 'organizationId',
    idFormat: 'intOrNone',
    minTier: 'basic',
    permissions: HR_READ,
    label: 'bi.drill.kind.hr.organization'
  },
  'hr.jobPosition': {
    domain: 'hr',
    plural: 'positions',
    facet: 'jobPositionId',
    idFormat: 'intOrNone',
    minTier: 'basic',
    permissions: HR_READ,
    label: 'bi.drill.kind.hr.jobPosition'
  },
  'hr.workLocation': {
    domain: 'hr',
    plural: 'locations',
    facet: 'workLocationId',
    idFormat: 'intOrNone',
    minTier: 'basic',
    permissions: HR_READ,
    label: 'bi.drill.kind.hr.workLocation'
  },

  /* ── Түгээлт — машин (хөрөнгө). Багийн бүрэлдэхүүн (хүн) drill БИШ. ── */
  'delivery.vehicle': {
    domain: 'delivery',
    plural: 'vehicles',
    facet: 'vehicleId',
    idFormat: 'int',
    minTier: 'basic',
    permissions: ['delivery.runs:list'],
    label: 'bi.drill.kind.delivery.vehicle'
  },

  /* ── Агуулах — 1С үлдэгдлийн facet (`warehouse`, `brand`, `productGroup` — `/erp/bi/stock*`-д ШИНЭ, API). ── */
  'warehouse.warehouse': {
    domain: 'warehouse',
    plural: 'warehouses',
    facet: 'warehouse',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: ['erp.warehouses:list'],
    label: 'bi.drill.kind.warehouse.warehouse'
  },
  'warehouse.brand': {
    domain: 'warehouse',
    plural: 'brands',
    facet: 'brand',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: ['erp.warehouses:list'],
    label: 'bi.drill.kind.warehouse.brand'
  },
  'warehouse.productGroup': {
    domain: 'warehouse',
    plural: 'product-groups',
    facet: 'productGroup',
    idFormat: 'uuid',
    minTier: 'basic',
    permissions: ['erp.warehouses:list'],
    label: 'bi.drill.kind.warehouse.productGroup'
  },

  /* ── Санхүү — харилцагч (`customer` facet `/erp/bi/finance/*`-д ШИНЭ, API). Санхүү бүхэлдээ ≥ Менежер (D7-1 d, D8-7). ── */
  'finance.customer': {
    domain: 'finance',
    plural: 'customers',
    facet: 'customer',
    idFormat: 'uuid',
    minTier: 'manager',
    permissions: ['erp.credit:list', 'erp.credit:aggregate'],
    label: 'bi.drill.kind.finance.customer'
  }
} as const satisfies Record<string, BiDrillKindSpec>

export type BiDrillKind = keyof typeof BI_DRILL_KINDS

export const BI_DRILL_KIND_KEYS = Object.keys(BI_DRILL_KINDS) as BiDrillKind[]

export const isBiDrillKind = (value: unknown): value is BiDrillKind =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(BI_DRILL_KINDS, value)

/** Төрлийн тодорхойлолт (өргөн төрлөөр — `as const`-ийн literal-ийг дуудагч мэдэх шаардлагагүй). */
export const biDrillKind = (kind: BiDrillKind): BiDrillKindSpec => BI_DRILL_KINDS[kind]

/** `/bi/{domain}/{plural}` → төрөл; олдохгүй бол `null` (маршрут `notFound()` — үсгийн алдаа 404, хоосон дэлгэц БИШ). */
export const biDrillKindOf = (domain: string, plural: string): BiDrillKind | null =>
  BI_DRILL_KIND_KEYS.find(kind => BI_DRILL_KINDS[kind].domain === domain && BI_DRILL_KINDS[kind].plural === plural) ??
  null

/** Тухайн домэйны төрлүүд (табын шошго, AI каталог). */
export const biDrillKindsOf = (domain: BiDomainKey): BiDrillKind[] =>
  BI_DRILL_KIND_KEYS.filter(kind => BI_DRILL_KINDS[kind].domain === domain)

/**
 * ТАНИХ ТЭМДГИЙГ ХЭВИЙН БОЛГОНО — uuid жижиг үсэг (lookups/breakdown-той ижил), бусад хэвээр. Хэлбэр буруу бол `null`
 * (маршрут `notFound()`; сервер рүү хог id явуулахгүй — DTO 400-гийн оронд).
 */
export const normalizeBiDrillId = (kind: BiDrillKind, raw: string): string | null => {
  const id = raw.trim()

  switch (BI_DRILL_KINDS[kind].idFormat) {
    case 'uuid': {
      const lower = id.toLowerCase()

      return UUID_RE.test(lower) ? lower : null
    }

    case 'int':
      return INT_RE.test(id) ? id : null

    case 'intOrNone':
      return id === BI_DRILL_NONE_ID || INT_RE.test(id) ? id : null
  }
}

/* ─────────────────────────── Хаяг ─────────────────────────── */

/** BI-ийн самбарын үндсэн зам (`/bi/sales`). */
export const biBoardPath = (domain: BiDomainKey): string => `/bi/${domain}`

/**
 * DRILL-ИЙН ЗАМ (query-гүй). Query-г (хугацаа, шүүлт, түвшин) дуудагч ОДООГИЙН хаягаас залгана — таб солиход контекст хэвээр.
 * ! `id` нь `normalizeBiDrillId`-ээр шалгагдсан байх ёстой (энд дахин шалгаж, буруу бол ШИДНЭ — чимээгүй буруу холбоос үүсгэхгүй).
 */
export const biDrillPath = (kind: BiDrillKind, id: string): string => {
  const normalized = normalizeBiDrillId(kind, id)

  if (normalized === null) throw new Error(`bi-drill: буруу id «${id.slice(0, 40)}» (${kind})`)

  const spec = BI_DRILL_KINDS[kind]

  return `/bi/${spec.domain}/${spec.plural}/${encodeURIComponent(normalized)}`
}

export type BiDrillRef = { kind: BiDrillKind; id: string }

/**
 * ЗАМЫГ ЗАДЛАНА: `/bi/{domain}/{plural}/{id}` (query, hash, төгсгөлийн `/` зөвшөөрнө) → `{kind, id}`; самбар эсвэл танихгүй → `null`.
 * ! `basePath`-гүй зам (Next-ийн `usePathname`) хүлээнэ.
 */
export const parseBiDrillPath = (path: string): BiDrillRef | null => {
  const clean = path.replace(/[?#].*$/, '').replace(/\/+$/, '')
  const match = /^\/bi\/([a-z]+)\/([a-z-]+)\/([^/]+)$/.exec(clean)

  if (!match || !(BI_DOMAIN_KEYS as readonly string[]).includes(match[1]!)) return null

  const kind = biDrillKindOf(match[1]!, match[2]!)

  if (!kind) return null

  let decoded: string

  try {
    decoded = decodeURIComponent(match[3]!)
  } catch {
    return null
  }

  const id = normalizeBiDrillId(kind, decoded)

  return id === null ? null : { kind, id }
}

/* ─────────────────────────── Таб ─────────────────────────── */

/**
 * НЭЭЛТТЭЙ DRILL ТАБЫН ДЭЭД ТОО (самбарын таб ТООЛОГДОХГҮЙ). Хэтэрвэл ХАМГИЙН ЭРТ идэвхжсэн, ИДЭВХГҮЙ таб хасагдана (docs/31 §4v.4).
 * ! Табын жагсаалт = үзэгч бүрийн тав тух (`localStorage`, try/catch); идэвхтэй таб = URL.
 */
export const BI_DRILL_MAX_TABS = 8

/* ─────────────────────────── Бараа, харилцагчийн лавлах (async шүүлт + толгойн карт) ─────────────────────────── */

/**
 * `/erp/1c-detail`-ийн ШИНЭ facet (docs/31 §4v.5) — BI-ийн бүх борлуулалтын нэгтгэл, жагсаалт, нийлбэр, Excel НЭГ `filtersOf`-оор
 * (Ш9: BI-ийн нийлбэр = жагсаалтын нийлбэр). Олон мянган утгатай тул EAGER лавлахад (`/erp/1c-detail/lookups`) ОРОХГҮЙ —
 * async хайлт (`lookups/products`, `lookups/customers`).
 *   `product`  — `Номенклатура` (`_Reference139._IDRRef`) — мөрийн бараа (`vt`), 1c-detail-ийн `prRef`;
 *   `customer` — `Партнер` (`_Fld10990RRef` борлуулалт / буцаалтын харилцагч) — breakdown `by=customer`-ийн id.
 */
export const BI_SALES_ENTITY_FACETS = ['product', 'customer'] as const

export type BiSalesEntityFacet = (typeof BI_SALES_ENTITY_FACETS)[number]

/** Лавлахын цэг (`GET /erp/1c-detail/lookups/<kind>`) — `products` | `customers`. */
export const BI_ENTITY_LOOKUP_KINDS = ['products', 'customers'] as const

export type BiEntityLookupKind = (typeof BI_ENTITY_LOOKUP_KINDS)[number]

/** `?q=` хайлтын хамгийн бага урт (1 тэмдэгтээр 1С-д LIKE явуулахгүй). */
export const BI_ENTITY_LOOKUP_MIN_QUERY = 2

export const BI_ENTITY_LOOKUP_DEFAULT_LIMIT = 20

export const BI_ENTITY_LOOKUP_MAX_LIMIT = 50

/** `?ids=` — chip-ийн нэр тайлах (URL-ээс ирсэн сонголт); дээд тоо. */
export const BI_ENTITY_LOOKUP_MAX_IDS = 100

/**
 * Лавлахын нэг мөр. `name` — `_Description` (цэвэрлэсэн); `code` — `_Code`; `hint` — ялгах нэмэлт (бараа: артикул · брэнд;
 * харилцагч: суваг · бүс). Мөнгө ХЭЗЭЭ Ч ОРОХГҮЙ (Basic-д нээлттэй).
 */
export type BiEntityLookupItem = { id: string; name: string; code: string | null; hint: string | null }

/**
 * `GET /erp/1c-detail/lookups/{products|customers}?q=&limit=` эсвэл `?ids=a,b` хариу. `truncated` — `limit`-ээс олон тохирол
 * байсан (хэрэглэгч хайлтаа нарийсгана). 1С тасарвал 503 (`offline: true`), хоосон жагсаалт БИШ.
 */
export type BiEntityLookupResponse = { data: BiEntityLookupItem[]; truncated: boolean; offline: false }

/** Лавлахын нэр — id + нэр (толгойн картын шинж). */
export type BiEntityRef = { id: string; name: string | null }

/**
 * `GET /erp/1c-detail/lookups/products/:id` — барааны ТОЛГОЙН КАРТ (drill хуудасны дээд хэсэг, табын шошго). Мөнгөгүй.
 * Олдохгүй → 404 `BI_ENTITY_NOT_FOUND`.
 */
export type BiProductCard = {
  id: string
  code: string | null
  name: string
  article: string | null
  unit: BiEntityRef | null
  brand: BiEntityRef | null
  productGroup: BiEntityRef | null
  manufacturer: BiEntityRef | null
  asOf: string
}

/**
 * `GET /erp/1c-detail/lookups/customers/:id` — харилцагчийн (Партнер) ТОЛГОЙН КАРТ. Мөнгөгүй.
 * ! Хүрээ: Менежер / сувгийн хуваарилалттай хэрэглэгчид харилцагчийн суваг хүрээнд БИШ бол — байхгүйтэй ИЖИЛ 404
 * ! `BI_ENTITY_NOT_FOUND` (оршихуйг задлахгүй, AUTHZ-12). Хуудас тоо татахгүй, «Олдсонгүй эсвэл таны хүрээнд биш» гэнэ.
 */
export type BiCustomerCard = {
  id: string
  code: string | null
  name: string
  salesChannel: BiEntityRef | null
  outletType: BiEntityRef | null
  partnerType: BiEntityRef | null
  businessRegion: BiEntityRef | null
  deliveryZone: BiEntityRef | null
  asOf: string
}

/** 404-ийн `code` — байхгүй ба хүрээнээс гадуур ХОЁУЛАА (ялгахгүй). */
export const BI_ENTITY_NOT_FOUND_CODE = 'BI_ENTITY_NOT_FOUND' as const

/* ─────────────────────────── Хэрэглэгдсэн промо (docs/47 §2.4 холимог арга) ─────────────────────────── */

/**
 * `GET /erp/1c-detail/promos` — BI түлхүүр `erp.1c-detail.promos` (API нэмнэ: `BI_ROUTE_KEYS` + `BI_ROUTE_TIERS` ←
 * `BI_PROMOS_ROUTE_POLICY`, `@BiRoute`, OpenAPI, docs/48). Сар бүрийн БИЕЛСЭН промо: мөр бүрд (а) борлуулалтын `VT11066`
 * (`_Fld11068 = _Fld11055`), (б) ЗӨВХӨН тэр мөрөнд байхгүй бол холбоотой захиалгын `VT7874` (ижил КлючСвязи, масштаблаж
 * `_Fld11052` руу тэнцүүлнэ); «Бонус оноо» (төлбөрийн хэлбэр), промогүй гар хөнгөлөлт (`_Fld11051`), тайлбарлагдаагүй үлдэгдэл
 * — ТУСДАА сав. Join БҮРД `_Fld701 = 0`. Буцаалт (§2.4 д.6) — эх борлуулалтын мөрийн промоос тоогоор хувь тэнцүүлж хасна.
 */
export const BI_PROMO_BUCKETS = ['bonusPoints', 'manual', 'unexplained'] as const

export type BiPromoBucket = (typeof BI_PROMO_BUCKETS)[number]

/** Промоны хэрэглээ: сагс (захиалгын босго «5.000.000 OS») / SKU («5+1 HO CAN») / тодорхойгүй (§2.4 д.5). */
export const BI_PROMO_SCOPES = ['basket', 'sku', 'unknown'] as const

export type BiPromoScope = (typeof BI_PROMO_SCOPES)[number]

/**
 * Сар × промоны хэмжигдэхүүн. `amount` — хөнгөлөлтийн ₮ (НӨАТ-тай, мөнгө → Basic-д `null` + `redacted`); `lines` — промо
 * хэрэглэгдсэн борлуулалтын мөр; `documents` — падаан; `qty` — тэр мөрүүдийн тоо хэмжээ (нэг мөрд олон промо бол промо БҮРД
 * тоологдоно — промоор нэмбэл давхар; `totals.qty` нь давхардалгүй).
 */
export type BiPromoMeasures = { amount: number | null; lines: number; documents: number; qty: number }

export type BiPromoMonth = BiPromoMeasures & { month: string }

export type BiPromoRow = {
  /** `_Reference211._IDRRef` (промоны таних тэмдэг — нэр дэх огноо худал, §2.4 д.4). */
  id: string
  label: string | null
  campaign: BiEntityRef | null
  scope: BiPromoScope
  months: BiPromoMonth[]
  totals: BiPromoMeasures
}

/**
 * Хариуны мөнгөн талбар — хэмжигдэхүүнд `amount`; `coverage.explainedShare` — ₮-өөс бодсон ХАРЬЦАА (1 − |тайлбаргүй ₮| / (промо ₮ +
 * бонус ₮ + тайлбаргүй ₮)) → мөнгө гэж үзнэ (Basic = тоо ширхэг л; мөнгөөс гарсан хувь/эрэмбэ ч үгүй — docs/31 §4t).
 */
export const BI_PROMO_MONEY_FIELDS = ['amount', 'explainedShare'] as const

/**
 * Буулгалтын контейнер — `promos[].months[]`, `promos[].totals`, `buckets.<b>.months[]|totals`, `totals`, `totalsByMonth[]`,
 * `other.totals`, `coverage`.
 */
export const BI_PROMOS_REDACT: BiRedactSpec = {
  containers: [
    'totals',
    'totalsByMonth[]',
    'promos[].totals',
    'promos[].months[]',
    'other.totals',
    ...BI_PROMO_BUCKETS.flatMap(bucket => [`buckets.${bucket}.totals`, `buckets.${bucket}.months[]`]),
    'coverage'
  ],
  fields: BI_PROMO_MONEY_FIELDS
}

/**
 * Цэгийн бодлогын ТӨСӨЛ — API-ийн барилгачин `BI_ROUTE_TIERS['erp.1c-detail.promos']`-д ЯГ ЭНИЙГ тавина (docs/31 §4v.6):
 * борлуулалтын домэйн, баг/менежерийн шүүлт Pro, мөнгө буулгана, Менежерт сувгийн дотор мөнгө (`applied.salesChannel` post-assert).
 */
export const BI_PROMOS_ROUTE_POLICY = {
  method: 'GET',
  path: 'erp/1c-detail/promos',
  domain: 'sales',
  /* ! `manager` — docs/31 §4w-ээр хасагдсан (400 `BI_FILTER_RETIRED`), Basic-д 403 түрүүлж өгөх хаалга хэвээр. */
  proFilters: ['salesManager', 'manager'],
  redact: BI_PROMOS_REDACT,
  manager: 'money'
} as const

/**
 * `/erp/1c-detail/promos` хариу. `months` — цонхны сарууд (≤ 13); `incompleteLast` — сүүлийн сар MTD.
 * `other` — `limit`-ээс гадуурх промо нэгтгэсэн. `totalsByMonth` — БҮХ промо (Top-N + `other`) сараар: Σ `amount` = `totals.amount`
 * (сарын график үүгээр — Top-N-ийн нийлбэр биш); мөр/падаан/тоо давхардалгүй. `coverage.explainedShare` — автомат хөнгөлөлтийн
 * тайлбарлагдсан хувь (0…1; ₮-өөс бодсон тул Basic-д `null` + `redacted`).
 * Хүрээний талбарууд (`applied`, `dropped`, `scopeFallback`, `scoped`, `asOf`, `cached`, `throttled`) — бусад 1c-detail BI цэгтэй ИЖИЛ.
 */
export type BiPromosResponse = {
  window: { from: string; to: string }
  months: string[]
  incompleteLast: boolean
  limit: number
  promos: BiPromoRow[]
  other: { count: number; totals: BiPromoMeasures } | null
  buckets: Record<BiPromoBucket, { months: BiPromoMonth[]; totals: BiPromoMeasures }>
  totals: BiPromoMeasures
  totalsByMonth: BiPromoMonth[]
  method: 'hybrid-sale-vt11066-order-vt7874'
  returnsNetted: boolean
  coverage: { explainedShare: number | null }
  applied: Partial<Record<string, string[]>>
  dropped: Partial<Record<string, string[]>>
  scopeFallback: string[]
  scoped: boolean
  asOf: string
  cached: boolean
  throttled: boolean
  offline: false
}

/** Промоны цонхны хязгаар: `product` эсвэл `customer` facet-гүй хүсэлт ≤ 3 сар (бүх SKU × сар ≈ 1.3 с — docs/47 §2.4 д.8). */
export const BI_PROMOS_UNFOCUSED_MAX_MONTHS = 3

/**
 * Цонхны ХУАНЛИЙН сарын тоо (`YYYY-MM-DD` мөр — `Date` байхгүй): 06-20…09-10 = 4 сар. API (`assertPromosWindow`) ба админ
 * (`promosWindowOk`) ХОЁУЛАА үүгээр — клиент урьдчилан хэлэх ба серверийн 400 нэг дүрэм.
 */
export const biPromosMonthsSpanned = (window: { from: string; to: string }): number =>
  (Number(window.to.slice(0, 4)) - Number(window.from.slice(0, 4))) * 12 +
  (Number(window.to.slice(5, 7)) - Number(window.from.slice(5, 7))) +
  1
